import { useCallback, useState } from 'react';
import isNil from 'lodash/isNil';
import { fetchQuery, graphql } from 'react-relay';
import { createOperationDescriptor } from 'relay-runtime';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { type FlagConfiguration, useFlagService } from '@atlassian/jira-flags';
import getRelayEnvironment from '@atlassian/jira-relay-environment';
import useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery, {
	type useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery as useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQueryType,
	type useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery$data,
} from '@atlassian/jira-relay/src/__generated__/useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { FETCH_CONNECTED_PROJECTS, PACKAGE_NAME, TEAM_NAME } from '../../common/errors-constants';
import { messages } from '../../common/messages';
import type { ConnectedProject } from '../use-connected-projects/types';

const GENERIC_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.connectedProjectsLoadFailureTitle,
	description: messages.projectConnectionUnexpectedFailureDescription,
};

type State = {
	data: ConnectedProject[];
	loading: Boolean;
	error: Error | null;
};

export const useFetchConnectedProjects = () => {
	const cloudId = useCloudId();
	const [{ data, loading, error }, setState] = useState<State>({
		data: [],
		loading: false,
		error: null,
	});
	const { showFlag } = useFlagService();
	const reportError = (reportedError: Error) => {
		fireErrorAnalytics({
			meta: {
				id: FETCH_CONNECTED_PROJECTS,
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error: reportedError,
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const fetchConnectedProjects = useCallback(
		async (currentProjectId: string): Promise<ConnectedProject[]> => {
			const currentProjectAri = JiraProjectAri.create({
				siteId: cloudId,
				projectId: currentProjectId,
			}).toString();
			let connectedProjectsResponse:
				| useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery$data
				| undefined;
			setState((prev) => ({ ...prev, error: null, loading: true }));
			const variables = { id: currentProjectAri };

			try {
				const query = graphql`
					query useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery($id: ID!) {
						graphStore @optIn(to: "GraphStore") {
							projectHasRelatedWorkWithProject(id: $id)
								@optIn(to: "GraphStoreProjectHasRelatedWorkWithProject") {
								edges {
									node {
										... on JiraProject {
											projectId
											name @required(action: THROW)
											avatar {
												small
											}
										}
									}
								}
							}
						}
					}
				`;
				/**
				 * fetchQuery will NOT retain the data for the query, meaning that it is not guaranteed that the
				 * data will remain saved in the Relay store at any point after the request completes. We need to
				 * explicitly retain the operation to ensure it doesn't get deleted.
				 * See https://relay.dev/docs/api-reference/fetch-query/#behavior
				 * */
				getRelayEnvironment().retain(
					createOperationDescriptor(
						useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQuery,
						variables,
					),
				);

				const fetchCallObservable$ =
					fetchQuery<useFetchConnectedProjectsProjectHasRelatedWorkWithProjectQueryType>(
						getRelayEnvironment(),
						query,
						variables,
						{ fetchPolicy: 'store-or-network', networkCacheConfig: { force: false } },
					);

				const fetchCallPromise = fetchCallObservable$.toPromise();

				connectedProjectsResponse = await fetchCallPromise;
			} catch (err) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const reportedError = err as Error;

				reportError(reportedError);
				showFlag(GENERIC_ERROR_FLAG);
				setState({ data: [], loading: false, error: reportedError });
				throw reportedError;
			}

			if (isNil(connectedProjectsResponse?.graphStore?.projectHasRelatedWorkWithProject?.edges)) {
				const unknownError = new Error('Unexpected response while fetching connected projects');
				reportError(unknownError);
				showFlag(GENERIC_ERROR_FLAG);
				setState({
					data: [],
					loading: false,
					error: unknownError,
				});
				throw unknownError;
			}

			const projects: ConnectedProject[] =
				connectedProjectsResponse.graphStore.projectHasRelatedWorkWithProject.edges
					.map((edge) => {
						// there are rare cases where node might be null in the response (e.g. after manual interaction with GraphQL)
						if (!edge?.node?.projectId?.length || !edge?.node?.name?.length) {
							return null;
						}

						return {
							id: edge.node.projectId,
							name: edge.node.name,
							avatarUrl: edge?.node?.avatar?.small,
							resourceId: JiraProjectAri.create({
								siteId: cloudId,
								projectId: edge?.node?.projectId,
							}).toString(),
						};
					})
					.filter(Boolean) ?? [];

			setState({ data: projects, loading: false, error: null });
			return projects;
		},
		[cloudId, showFlag],
	);

	return { fetchConnectedProjects, data, loading, error } as const;
};
